import {
  CoverageAreaOrderDetailDto,
  CoverageAreaDriverRowDto,
} from "types/coverageAreaOrderDetail";
import {
  DealerDriverDto,
  isOnlineDriverStatus,
} from "types/dealerDrivers";

export type DriverSelectGroup = "none" | "map" | "other";

export const NO_DRIVER_OPTION_ID = "__no_driver__";

export interface DriverSelectOption {
  id: string;
  label: string;
  groupKey: DriverSelectGroup;
  nameForAssignment: string;
  enabled?: boolean;
  activeOrders?: number;
}

export interface DriverSelectOptionLabels {
  noDriver: string;
  available: string;
  onDelivery: string;
}

/*
type StatusLabels = Pick<DriverSelectOptionLabels, "available" | "onDelivery">;

function buildDriverLabelWithStatus(
  name: string,
  enabled: boolean | undefined,
  labels: StatusLabels,
): string {
  const base = name.trim();
  const suffix = enabled === false ? labels.onDelivery : labels.available;
  return `${base} - ${suffix}`;
}
*/

function buildDriverDisplayLabel(name: string): string {
  return name.trim();
}

function dealerDriverDisplayName(driver: DealerDriverDto): string {
  return (driver.name ?? "").trim();
}

function indexDriversById(
  drivers: DealerDriverDto[] | undefined,
): Map<string, DealerDriverDto> {
  const byId = new Map<string, DealerDriverDto>();
  (drivers ?? []).forEach((driver) => {
    byId.set(driver.id, driver);
  });
  return byId;
}

function displayNameForCoverageDriver(
  logisticsName: string | undefined,
  driverId: string,
  driversById: Map<string, DealerDriverDto>,
): string {
  const fromLogistics = (logisticsName ?? "").trim();
  const driver = driversById.get(driverId);
  const fromDriver = driver ? dealerDriverDisplayName(driver) : "";
  return (fromDriver || fromLogistics).trim();
}

function getCoverageAreaMapDriverRows(
  detail: CoverageAreaOrderDetailDto,
): CoverageAreaDriverRowDto[] {
  const driversFromField = detail.drivers ?? [];
  if (driversFromField.length === 0) {
    return [];
  }

  return [...driversFromField].sort(
    (a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0),
  );
}

function indexCoverageDriverEnabledById(
  detail: CoverageAreaOrderDetailDto | null,
): Map<string, boolean | undefined> {
  const byId = new Map<string, boolean | undefined>();
  if (!detail) {
    return byId;
  }

  getCoverageAreaMapDriverRows(detail).forEach((row) => {
    const id = row.details?.id?.trim();
    if (id) {
      byId.set(id, row.details.enabled);
    }
  });

  return byId;
}

function resolveDriverInsightFields(
  driverId: string,
  fallbackEnabled: boolean | undefined,
  activeOrdersById?: Map<string, number>,
): { enabled?: boolean; activeOrders?: number } {
  if (!activeOrdersById?.has(driverId)) {
    return { enabled: fallbackEnabled };
  }

  const activeOrders = activeOrdersById.get(driverId) ?? 0;
  return {
    activeOrders,
    enabled: activeOrders === 0,
  };
}

export function collectListedDriverIds(
  detail: CoverageAreaOrderDetailDto | null,
  drivers: DealerDriverDto[] | undefined,
): string[] {
  const onlineDriversById = indexDriversById(
    (drivers ?? []).filter((driver) => isOnlineDriverStatus(driver.status)),
  );
  const driverIds = new Set<string>(onlineDriversById.keys());

  if (detail) {
    getCoverageAreaMapDriverRows(detail).forEach((row) => {
      const mapDriverId = row.details?.id?.trim();
      if (mapDriverId && onlineDriversById.has(mapDriverId)) {
        driverIds.add(mapDriverId);
      }
    });
  }

  return Array.from(driverIds);
}

function pushDriverRowOptions(
  rows: { priority: number; option: DriverSelectOption }[],
  seenIds: Set<string>,
  sortedDrivers: CoverageAreaDriverRowDto[],
  driversById: Map<string, DealerDriverDto>,
  activeOrdersById?: Map<string, number>,
) {
  sortedDrivers.forEach((entry) => {
    const row = entry.details;
    const driverId = (row?.id ?? "").trim();
    const driverName = (row?.name ?? "").trim();
    const resolvedId = driverId || driverName;
    const dealerDriver = driversById.get(resolvedId);

    if (
      !resolvedId ||
      seenIds.has(resolvedId) ||
      !dealerDriver ||
      !isOnlineDriverStatus(dealerDriver.status)
    ) {
      return;
    }

    const name = displayNameForCoverageDriver(
      row?.name,
      resolvedId,
      driversById,
    );
    if (!name) {
      return;
    }

    seenIds.add(resolvedId);
    const insightFields = resolveDriverInsightFields(
      resolvedId,
      row?.enabled,
      activeOrdersById,
    );
    rows.push({
      priority: Number(entry.priority ?? 0),
      option: {
        id: resolvedId,
        label: buildDriverDisplayLabel(name),
        nameForAssignment: name,
        groupKey: "map",
        enabled: insightFields.enabled,
        activeOrders: insightFields.activeOrders,
      },
    });
  });
}

function collectMapDrivers(
  detail: CoverageAreaOrderDetailDto | null,
  driversById: Map<string, DealerDriverDto>,
  activeOrdersById?: Map<string, number>,
): DriverSelectOption[] {
  if (!detail) {
    return [];
  }
  const rows: { priority: number; option: DriverSelectOption }[] = [];
  const seenIds = new Set<string>();
  const sortedDrivers = getCoverageAreaMapDriverRows(detail);

  pushDriverRowOptions(
    rows,
    seenIds,
    sortedDrivers,
    driversById,
    activeOrdersById,
  );
  return rows.sort((a, b) => a.priority - b.priority).map((r) => r.option);
}

function buildOtherDrivers(
  drivers: DealerDriverDto[] | undefined,
  mapDriverIds: Set<string>,
  coverageEnabledById: Map<string, boolean | undefined>,
  activeOrdersById?: Map<string, number>,
): DriverSelectOption[] {
  return (drivers ?? [])
    .filter(
      (driver) =>
        isOnlineDriverStatus(driver.status) && !mapDriverIds.has(driver.id),
    )
    .map((driver) => {
      const displayName = dealerDriverDisplayName(driver);
      const fallbackEnabled =
        coverageEnabledById.get(driver.id) ?? driver.enabled;
      const insightFields = resolveDriverInsightFields(
        driver.id,
        fallbackEnabled,
        activeOrdersById,
      );
      return {
        id: driver.id,
        label: buildDriverDisplayLabel(displayName),
        nameForAssignment: displayName,
        groupKey: "other" as const,
        enabled: insightFields.enabled,
        activeOrders: insightFields.activeOrders,
      };
    });
}

export function buildDriverSelectOptions(
  detail: CoverageAreaOrderDetailDto | null,
  drivers: DealerDriverDto[] | undefined,
  labels: DriverSelectOptionLabels,
  activeOrdersById?: Map<string, number>,
): DriverSelectOption[] {
  const driversById = indexDriversById(drivers);
  const mapDrivers = collectMapDrivers(detail, driversById, activeOrdersById);
  const mapIds = new Set(mapDrivers.map((d) => d.id));
  const coverageEnabledById = indexCoverageDriverEnabledById(detail);
  const otherDrivers = buildOtherDrivers(
    drivers,
    mapIds,
    coverageEnabledById,
    activeOrdersById,
  );
  const noDriverOption: DriverSelectOption = {
    id: NO_DRIVER_OPTION_ID,
    label: labels.noDriver,
    nameForAssignment: "",
    groupKey: "none",
  };
  return [noDriverOption, ...mapDrivers, ...otherDrivers];
}
